import { useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Save, Trash2, X, Edit2 } from "lucide-react";
import { materialExtendedPrice, scaleCostRate, type CostRecipe } from "@workspace/api-zod/cost-recipe";
import type { CostRecipe as ApiCostRecipe } from "@workspace/api-client-react";
import {
  draftFromRecipe, draftMaterialLine, emptyDraft, evaluateDraft, newMaterialId,
  type MaterialDraft, type RecipeDraft,
} from "./cost-recipe-editor";
import { DimensionPricingConfig } from "./material-dimension-pricing";

/** Server-resolved description of a measured price, when present. */
function resolvedSpec(model: unknown): string | null {
  const v = model && typeof model === "object" ? (model as { resolved_spec?: unknown }).resolved_spec : null;
  return typeof v === "string" && v.trim() ? v : null;
}

type Fmt = (v: string | number) => string;
export type CostSection = "labour" | "materials" | "plant";
export const COST_SECTION_LABELS: Record<CostSection, string> = { labour: "Labour", materials: "Materials", plant: "Plant" };

/** Display subtotal: sum the same penny-rounded costs shown in the selected section. */
export function costSectionTotal(recipe: ApiCostRecipe, section: CostSection): string {
  if (section === "labour") return Number(recipe.labour_allowance).toFixed(2);
  if (section === "plant") return Number(recipe.plant_allowance ?? 0).toFixed(2);
  const supplyPence = recipe.shape === "specification" ? Math.round((recipe.supply_allowance ?? 0) * 100) : 0;
  const materialsPence = recipe.materials.reduce((sum, material) => sum + Math.round(Number(materialExtendedPrice(
    material.quantity_per_unit ?? material.base_factor, material.unit_price,
  )) * 100), 0);
  return ((supplyPence + materialsPence) / 100).toFixed(2);
}

/** Display-only quantity formatting: up to 4dp, trailing zeros trimmed (keeps 0.0275). */
export function fmtQty(n: number): string {
  return n.toFixed(4).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
}

export function rateWithUnit(fmt: Fmt, price: string | number, unit: string): string {
  const u = unit.trim();
  if (!u) return fmt(price);
  return u.toLowerCase() === "each" ? `${fmt(price)} each` : `${fmt(price)}/${u}`;
}

export function workingText(base: number, waste: number, qty: number): string {
  return waste > 0 ? `${fmtQty(base)} + ${fmtQty(waste)}% waste = ${fmtQty(qty)}` : fmtQty(qty);
}

export function joinAssumptions(list: string[]): string {
  return list.map(a => a.trim()).filter(Boolean).join(" · ");
}

// Keep complete utility names literal so Tailwind includes the narrow-screen rules.
const GRID = "grid grid-cols-[minmax(0,1.5fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(5.5rem,0.7fr)] gap-x-4 max-[430px]:grid-cols-[minmax(0,1fr)_auto] max-[430px]:gap-x-3";
const ROW = `${GRID} items-start border-b px-4 py-4 text-sm max-[430px]:px-3 max-[430px]:py-3`;
const C_ITEM = "min-w-0 break-words max-[430px]:row-start-1";
const C_WORK = "min-w-0 break-words tabular-nums max-[430px]:row-start-2 max-[430px]:col-start-1";
const C_RATE = "min-w-0 break-words tabular-nums max-[430px]:row-start-3 max-[430px]:col-start-1 max-[430px]:text-xs max-[430px]:text-muted-foreground";
const C_COST = "min-w-0 text-right tabular-nums max-[430px]:col-start-2 max-[430px]:row-start-1 max-[430px]:row-span-3";
const EMPTY = "max-[430px]:hidden";

function Row({ testId, item, working, rate, cost }: { testId: string; item: ReactNode; working?: ReactNode; rate?: ReactNode; cost: ReactNode }) {
  return (
    <div role="row" className={ROW} data-testid={testId}>
      <div role="cell" className={C_ITEM}>{item}</div>
      <div role="cell" className={`${C_WORK} ${working ? "" : EMPTY}`}>{working}</div>
      <div role="cell" className={`${C_RATE} ${rate ? "" : EMPTY}`}>{rate}</div>
      <div role="cell" className={C_COST}>{cost}</div>
    </div>
  );
}

function HeadRow() {
  return (
    <div role="row" className={`${GRID} items-center border-b px-4 h-12 text-sm font-medium text-muted-foreground max-[430px]:px-3 max-[430px]:h-10`}>
      <div role="columnheader">Item</div>
      <div role="columnheader" className={EMPTY}>Working</div>
      <div role="columnheader" className={EMPTY}>Rate</div>
      <div role="columnheader" className="text-right">Cost</div>
    </div>
  );
}

function TotalRow({ taskId, value, testId, section }: { taskId: string; value: ReactNode; testId: string; section?: CostSection }) {
  return (
    <div role="row" className="flex items-baseline justify-between gap-4 border-t-2 border-foreground/15 px-4 py-4 text-sm font-semibold max-[430px]:px-3 max-[430px]:py-3" data-testid={`row-breakdown-total-${taskId}`}>
      <div role="cell" aria-colspan={3}>{section ? `${COST_SECTION_LABELS[section]} total` : "Typical rate"}</div>
      <div role="cell" className="text-right tabular-nums" data-testid={testId}>{value}</div>
    </div>
  );
}

function Header({ unitLabel, right, section, lineQuantity }: { unitLabel: string; right: ReactNode; section?: CostSection; lineQuantity?: number }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="text-sm font-medium">{section ? COST_SECTION_LABELS[section] : "Breakdown"} <span className="text-muted-foreground">· {lineQuantity != null ? `for ${fmtQty(lineQuantity)} ${unitLabel}` : `per ${unitLabel}`}</span></div>
      <div className="flex items-center gap-2">{right}</div>
    </div>
  );
}

const TABLE_WRAP = "rounded-md border bg-card min-w-0 [&_[role=row]:last-child]:border-b-0";

interface ViewProps {
  section?: CostSection;
  taskId: string;
  unitLabel: string;
  recipe: ApiCostRecipe;
  rateTypical: string;
  sourceLabel: "Default" | "Your version";
  formatCurrency: Fmt;
  onEdit: () => void;
  children?: ReactNode;
  showAssumptions?: boolean;
  /** Quote lines: show costs for the whole line (recipe × quantity), not per unit. */
  lineQuantity?: number;
  /** Line totals already allocated to the quote (labour/materials/plant), so totals match exactly. */
  lineTotals?: { labourCost: number; materialCost: number; plantCost: number };
}

export function CostBreakdownView({ taskId, unitLabel, recipe, rateTypical, sourceLabel, formatCurrency: fmt, onEdit, children, section, showAssumptions = true, lineQuantity, lineTotals }: ViewProps) {
  const assumptions = joinAssumptions(recipe.materials.map(m => m.assumption));
  const q = lineQuantity ?? 1;
  const q4 = (n: number) => Math.round(n * 10000) / 10000;
  const perLine = lineQuantity != null;
  const sectionTotal = (s: CostSection) => !lineTotals ? costSectionTotal(recipe, s)
    : (s === "labour" ? lineTotals.labourCost : s === "materials" ? lineTotals.materialCost : lineTotals.plantCost).toFixed(2);
  return (
    <div className="space-y-3 min-w-0" data-testid={`panel-breakdown-${taskId}`}>
      <Header section={section} unitLabel={unitLabel} lineQuantity={lineQuantity} right={<>
        <Badge variant={sourceLabel === "Your version" ? "default" : "secondary"} className="font-normal" data-testid={`badge-recipe-source-${taskId}`}>{sourceLabel}</Badge>
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`button-edit-breakdown-${taskId}`}>
          <Edit2 className="h-4 w-4 mr-1" /> Edit
        </Button>
      </>} />
      <div role="table" aria-label={`Breakdown per ${unitLabel}`} className={TABLE_WRAP} data-testid={`table-breakdown-${taskId}`}>
        <HeadRow />
        {(!section || section === "materials") && recipe.shape === "specification" && (() => {
          const supplyName = (recipe as { supply_name?: string }).supply_name || "Supply";
          const pricing = recipe.supply_pricing;
          const unitAllowance = recipe.supply_allowance ?? 0;
          const allowance = perLine ? materialExtendedPrice(q, unitAllowance) : unitAllowance;
          if (pricing?.section_rates) {
            // Steel behaves like any other measured material: section, working, rate, cost.
            const qty = pricing.resolved_quantity != null ? q4(pricing.resolved_quantity * q) : undefined;
            const byKg = /kg$/.test(pricing.resolved_spec ?? "");
            const section = pricing.resolved_section?.replace(/^(\d+)X(\d+)X([\d.]+)(UB|UC)$/i, "$1 × $2 × $3 $4");
            const missing = recipe.quote_pricing?.status === "needs_measurements"
              ? recipe.quote_pricing.notes.find(n => n.startsWith(`${supplyName}:`))?.slice(supplyName.length + 1).trim() : null;
            return <Row testId={`row-breakdown-supply-${taskId}`}
              item={<>
                <div className="font-medium" data-testid={`text-supply-name-${taskId}`}>Steel Beam</div>
                {section && <div className="text-xs text-muted-foreground" data-testid={`text-supply-section-${taskId}`}>{section}</div>}
                {!qty && missing && <div className="text-xs text-muted-foreground" data-testid={`text-supply-unresolved-${taskId}`}>{missing}</div>}
              </>}
              working={qty ? <span data-testid={`text-supply-resolved-${taskId}`}>{fmtQty(qty)} {byKg ? "kg" : "m"}</span> : undefined}
              rate={qty ? rateWithUnit(fmt, (unitAllowance / pricing.resolved_quantity!).toFixed(2), byKg ? "kg" : "m") : fmt(unitAllowance)}
              cost={<span data-testid={`text-supply-${taskId}`}>{fmt(allowance)}</span>} />;
          }
          return <Row testId={`row-breakdown-supply-${taskId}`}
            item={<>
              <div className="font-medium" data-testid={`text-supply-name-${taskId}`}>{supplyName}</div>
              <div className="text-xs text-muted-foreground" data-testid={`text-specification-${taskId}`}>{recipe.specification}</div>
              {resolvedSpec(pricing) && (
                <div className="text-xs text-muted-foreground" data-testid={`text-supply-resolved-${taskId}`}>{resolvedSpec(pricing)}</div>
              )}
            </>}
            working={perLine ? fmtQty(q) : undefined}
            rate={fmt(unitAllowance)}
            cost={<span data-testid={`text-supply-${taskId}`}>{fmt(allowance)}</span>} />;
        })()}
        {(!section || section === "materials") && recipe.materials.map(m => {
          const qty = q4((m.quantity_per_unit ?? m.base_factor) * q);
          return (
            <Row key={m.material_id} testId={`row-material-${m.material_id}`}
              item={<>
                <div className="font-medium">{m.name}</div>
                {m.spec ? <div className="text-xs text-muted-foreground">{m.spec}</div> : null}
                {resolvedSpec((m as { dimension_pricing?: unknown }).dimension_pricing) && (
                  <div className="text-xs text-muted-foreground" data-testid={`text-material-resolved-${m.material_id}`}>{resolvedSpec((m as { dimension_pricing?: unknown }).dimension_pricing)}</div>
                )}
              </>}
              working={workingText(q4(m.base_factor * q), m.waste_pct, qty)}
              rate={rateWithUnit(fmt, m.unit_price, m.unit)}
              cost={fmt(materialExtendedPrice(qty, m.unit_price))} />
          );
        })}
        {(!section || section === "labour") && <Row testId={`row-breakdown-labour-${taskId}`} item={<span className="font-medium">Labour</span>}
          cost={<span data-testid={`text-labour-${taskId}`}>{fmt(lineTotals ? lineTotals.labourCost : recipe.labour_allowance * q)}</span>} />}
        {(!section || section === "plant") && ((recipe.plant_allowance ?? 0) > 0 || section === "plant") && (
          <Row testId={`row-breakdown-plant-${taskId}`} item={<span className="font-medium">Plant</span>}
            cost={<span data-testid={`text-plant-${taskId}`}>{fmt(lineTotals ? lineTotals.plantCost : (recipe.plant_allowance ?? 0) * q)}</span>} />
        )}
        <TotalRow taskId={taskId} section={section} value={fmt(section ? sectionTotal(section) : Number(rateTypical) * q)}
          testId={section ? `text-section-total-${taskId}` : `text-derived-typical-${taskId}`} />
      </div>
      {section === "materials" && recipe.shape === "formula" && recipe.materials.length === 0 && <p className="text-sm text-muted-foreground">No materials added.</p>}
      {section === "plant" && !(Number(recipe.plant_allowance) > 0) && <p className="text-sm text-muted-foreground">No plant allowance added.</p>}
      {showAssumptions && (!section || section === "materials") && assumptions && (
        <p className="text-xs text-muted-foreground break-words" data-testid={`text-assumptions-${taskId}`}>Assumes: {assumptions}</p>
      )}
      {children}
    </div>
  );
}

function Err({ msg, id }: { msg?: string; id: string }) {
  if (!msg) return null;
  return <p className="text-xs text-destructive mt-1" data-testid={`error-${id}`}>{msg}</p>;
}

interface EditProps {
  section?: CostSection;
  currentLow?: string;
  currentHigh?: string;
  taskId: string;
  unitLabel: string;
  initial: ApiCostRecipe | null;
  currentTypical: string;
  formatCurrency: Fmt;
  isSaving: boolean;
  serverError?: string | null;
  isFirstAdd: boolean;
  onCancel: () => void;
  onSave: (recipe: CostRecipe) => void;
  sourceLabel?: "Default" | "Your version";
  children?: ReactNode;
}

const IN = "h-8 min-w-0 max-w-full text-sm tabular-nums";

export function CostBreakdownEdit({ taskId, unitLabel, initial, currentTypical, formatCurrency: fmt, isSaving, serverError, isFirstAdd, onCancel, onSave, sourceLabel, children, section, currentLow, currentHigh }: EditProps) {
  const [draft, setDraft] = useState<RecipeDraft | null>(initial ? draftFromRecipe(initial) : section && section !== "materials" ? { ...emptyDraft("formula"), labour_allowance: currentTypical } : null);
  const [attempted, setAttempted] = useState(false);
  const [showPlant, setShowPlant] = useState(() => Number(initial?.plant_allowance ?? 0) > 0);
  const evaluation = useMemo(() => (draft ? evaluateDraft(draft, "user_edited") : null), [draft]);

  const cancelBtn = (
    <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={isSaving} data-testid={`button-cancel-recipe-${taskId}`}>
      <X className="h-4 w-4 mr-1" /> Cancel
    </Button>
  );

  if (!draft) {
    return (
      <div className="space-y-3 min-w-0" data-testid={`editor-recipe-${taskId}`}>
        <Header section={section} unitLabel={unitLabel} right={cancelBtn} />
        <p className="text-sm font-medium">What kind of item is this?</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {(["formula", "specification"] as const).map(shape => (
            <button key={shape} type="button" onClick={() => setDraft({ ...emptyDraft(shape), ...(section ? { labour_allowance: currentTypical } : {}) })}
              className="text-left rounded-md border bg-card p-3 hover:bg-muted/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid={`button-shape-${shape}-${taskId}`}>
              <div className="font-medium text-sm">{shape === "formula" ? "Formula" : "Specification"}</div>
              <div className="text-xs text-muted-foreground">
                {shape === "formula"
                  ? `Materials with a quantity per ${unitLabel}, waste and unit price. Brickwork, plasterboard, tiling.`
                  : "An assumed spec plus a supply allowance. Windows, doors, boilers."}
              </div>
            </button>
          ))}
        </div>
        {children}
      </div>
    );
  }

  const d = draft;
  const errors = attempted && evaluation && !evaluation.ok ? evaluation.errors : {};
  const set = (patch: Partial<RecipeDraft>) => setDraft({ ...d, ...patch });
  const setMat = (i: number, patch: Partial<MaterialDraft>) =>
    setDraft({ ...d, materials: d.materials.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const addMat = () => setDraft({ ...d, materials: [...d.materials, {
    material_id: newMaterialId(d.materials.map(m => m.material_id)),
    name: "", spec: "", base_factor: "", waste_pct: "0", unit: "", unit_price: "", assumption: "",
  }] });
  const removeMat = (i: number) => setDraft({ ...d, materials: d.materials.filter((_, j) => j !== i) });

  const liveTypical = evaluation?.ok ? evaluation.rateTypical : null;
  const differs = isFirstAdd && liveTypical !== null && Number(liveTypical) !== Number(currentTypical);
  const plantVisible = showPlant || Number(d.plant_allowance) > 0 || !!errors["plant_allowance"];
  const supplyNum = Number(d.supply_allowance);
  let ratePreview: { low: string; high: string } | null = null;
  let ratePreviewError: string | null = null;
  if (liveTypical !== null && currentLow != null && currentHigh != null) {
    try {
      ratePreview = { low: scaleCostRate(currentLow, currentTypical, liveTypical),
        high: scaleCostRate(currentHigh, currentTypical, liveTypical) };
    } catch (error) { ratePreviewError = error instanceof Error ? error.message : "Could not update the price range."; }
  }

  function submit() {
    setAttempted(true);
    if (evaluation?.ok && !ratePreviewError) onSave(evaluation.recipe);
  }

  return (
    <div className="space-y-3 min-w-0" data-testid={`editor-recipe-${taskId}`}>
      <Header section={section} unitLabel={unitLabel} right={<>
        {sourceLabel && <Badge variant={sourceLabel === "Your version" ? "default" : "secondary"} className="font-normal" data-testid={`badge-recipe-source-${taskId}`}>{sourceLabel}</Badge>}
        {cancelBtn}
        <Button type="button" size="sm" onClick={submit} disabled={isSaving} data-testid={`button-save-recipe-${taskId}`}>
          {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </>} />
      <div role="table" aria-label={`Edit breakdown per ${unitLabel}`} className={TABLE_WRAP} data-testid={`table-breakdown-${taskId}`}>
        <HeadRow />
        {(!section || section === "materials") && d.shape === "specification" && (
          <Row testId={`row-breakdown-supply-${taskId}`}
            item={<>
              <Input aria-label="Supply item name (optional)" placeholder="Supply (name optional)" className={`${IN} font-medium mb-1`}
                value={d.supply_name ?? ""} onChange={e => set({ supply_name: e.target.value })} data-testid={`input-supply-name-${taskId}`} />
              <Err id={`supply-name-${taskId}`} msg={errors["supply_name"]} />
              <Textarea rows={2} aria-label="Assumed specification" className="text-sm min-h-0" value={d.specification}
                placeholder="uPVC casement 1200x1200, double glazed, white"
                onChange={e => set({ specification: e.target.value })} data-testid={`input-specification-${taskId}`} />
              <Err id={`specification-${taskId}`} msg={errors["specification"]} />
            </>}
            rate={<>
              <Input aria-label={`Supply allowance per ${unitLabel}`} inputMode="decimal" className={IN} value={d.supply_allowance}
                onChange={e => set({ supply_allowance: e.target.value })} data-testid={`input-supply-${taskId}`} />
              <Err id={`supply-${taskId}`} msg={errors["supply_allowance"]} />
            </>}
            cost={d.supply_allowance.trim() && Number.isFinite(supplyNum) ? fmt(supplyNum) : "—"} />
        )}
        {(!section || section === "materials") && d.materials.map((m, i) => {
          const line = draftMaterialLine(m);
          const p = `materials.${i}`;
          return (
            <Row key={m.material_id} testId={`row-material-edit-${m.material_id}`}
              item={<div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Input aria-label="Material name" placeholder="Name" className={`${IN} font-medium`} value={m.name}
                    onChange={e => setMat(i, { name: e.target.value })} data-testid={`input-material-name-${m.material_id}`} />
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label={`Remove ${m.name || "material"}`}
                    onClick={() => removeMat(i)} data-testid={`button-remove-material-${m.material_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Err id={`material-name-${m.material_id}`} msg={errors[`${p}.name`]} />
                <Input aria-label="Spec (optional)" placeholder="Spec (optional)" className={`${IN} text-xs`} value={m.spec}
                  onChange={e => setMat(i, { spec: e.target.value })} data-testid={`input-material-spec-${m.material_id}`} />
                <Err id={`material-spec-${m.material_id}`} msg={errors[`${p}.spec`]} />
              </div>}
              working={<div className="space-y-1">
                <div className="flex flex-wrap items-center gap-1">
                  <Input aria-label={`Quantity per ${unitLabel}`} inputMode="decimal" className={`${IN} w-20`} value={m.base_factor}
                    onChange={e => setMat(i, { base_factor: e.target.value })} data-testid={`input-material-base-${m.material_id}`} />
                  <span className="text-muted-foreground">+</span>
                  <Input aria-label="Waste %" inputMode="decimal" className={`${IN} w-14`} value={m.waste_pct}
                    onChange={e => setMat(i, { waste_pct: e.target.value })} data-testid={`input-material-waste-${m.material_id}`} />
                  <span className="text-muted-foreground">% =</span>
                  <span data-testid={`text-material-qty-${m.material_id}`}>{line ? line.qty : "—"}</span>
                </div>
                <Err id={`material-base-${m.material_id}`} msg={errors[`${p}.base_factor`]} />
                <Err id={`material-waste-${m.material_id}`} msg={errors[`${p}.waste_pct`]} />
              </div>}
              rate={<div className="space-y-1">
                <div className="flex flex-wrap items-center gap-1">
                  <Input aria-label="Unit price (GBP)" inputMode="decimal" className={`${IN} w-20`} value={m.unit_price}
                    onChange={e => setMat(i, { unit_price: e.target.value })} data-testid={`input-material-price-${m.material_id}`} />
                  <span className="text-muted-foreground">/</span>
                  <Input aria-label="Unit" placeholder="each" className={`${IN} w-16`} value={m.unit}
                    onChange={e => setMat(i, { unit: e.target.value })} data-testid={`input-material-unit-${m.material_id}`} />
                </div>
                <Err id={`material-price-${m.material_id}`} msg={errors[`${p}.unit_price`]} />
                <Err id={`material-unit-${m.material_id}`} msg={errors[`${p}.unit`]} />
              </div>}
              cost={<span data-testid={`text-material-line-${m.material_id}`}>{line ? fmt(line.extended) : "—"}</span>} />
          );
        })}
        {(!section || section === "labour") && <Row testId={`row-breakdown-labour-${taskId}`} item={<span className="font-medium">Labour</span>}
          cost={<div className="flex flex-col items-end">
            <Input aria-label={`Labour allowance per ${unitLabel}`} inputMode="decimal" className={`${IN} w-24 text-right`} value={d.labour_allowance}
              onChange={e => set({ labour_allowance: e.target.value })} data-testid={`input-labour-${taskId}`} />
            <Err id={`labour-${taskId}`} msg={errors["labour_allowance"]} />
          </div>} />}
        {(!section || section === "plant") && (plantVisible || section === "plant") && (
          <Row testId={`row-breakdown-plant-${taskId}`} item={<span className="font-medium">Plant</span>}
            cost={<div className="flex flex-col items-end">
              <Input aria-label={`Plant allowance per ${unitLabel}`} inputMode="decimal" className={`${IN} w-24 text-right`} value={d.plant_allowance}
                onChange={e => set({ plant_allowance: e.target.value })} data-testid={`input-plant-${taskId}`} />
              <Err id={`plant-${taskId}`} msg={errors["plant_allowance"]} />
            </div>} />
        )}
        <TotalRow taskId={taskId} section={section}
          value={evaluation?.ok ? fmt(section ? costSectionTotal(evaluation.recipe, section) : evaluation.rateTypical) : "Incomplete"}
          testId={section ? `text-live-section-total-${taskId}` : `text-live-typical-${taskId}`} />
      </div>
      {ratePreview && <p className="text-xs text-muted-foreground" data-testid={`text-rate-band-preview-${taskId}`}>
        Low {fmt(ratePreview.low)} · Typical {fmt(liveTypical!)} · High {fmt(ratePreview.high)}. All three rates update proportionally when you save.
      </p>}
      {ratePreviewError && <p className="text-sm text-destructive" role="alert">{ratePreviewError}</p>}

      <div className="flex flex-wrap gap-2">
        {(!section || section === "materials") && <Button type="button" variant="outline" size="sm" onClick={addMat} disabled={d.materials.length >= 100} data-testid={`button-add-material-${taskId}`}>
          <Plus className="h-4 w-4 mr-1" /> Add material
        </Button>}
        {!section && !plantVisible && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowPlant(true)} data-testid={`button-add-plant-${taskId}`}>
            <Plus className="h-4 w-4 mr-1" /> Add plant allowance
          </Button>
        )}
        {!initial && (!section || section === "materials") && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setDraft(null)} data-testid={`button-change-shape-${taskId}`}>
            Change type
          </Button>
        )}
      </div>
      <Err id={`materials-${taskId}`} msg={errors["materials"]} />

      {(!section || section === "materials") && (d.shape === "specification" || d.materials.length > 0) && (
        <div className="space-y-2" data-testid={`section-measured-pricing-edit-${taskId}`}>
          <div className="text-xs text-muted-foreground">Measured pricing (optional, future quotes only):</div>
          {d.shape === "specification" && (
            <DimensionPricingConfig id={`supply-${taskId}`} itemLabel={d.supply_name?.trim() || "supply"} value={d.supply_pricing}
              onChange={v => set({ supply_pricing: v })} errors={errors} errorPrefix="supply_pricing" />
          )}
          {d.materials.map((m, i) => (
            <DimensionPricingConfig key={m.material_id} id={m.material_id} itemLabel={m.name || "this material"} value={m.dimension_pricing}
              onChange={v => setMat(i, { dimension_pricing: v })} errors={errors} errorPrefix={`materials.${i}.dimension_pricing`} />
          ))}
        </div>
      )}

      {(!section || section === "materials") && d.materials.length > 0 && (
        <div className="space-y-2" data-testid={`section-assumptions-edit-${taskId}`}>
          <div className="text-xs text-muted-foreground">Assumes:</div>
          {d.materials.map((m, i) => (
            <div key={m.material_id} className="grid gap-1 sm:grid-cols-[10rem_minmax(0,1fr)] sm:items-center">
              <label htmlFor={`massume-${m.material_id}`} className="text-xs text-muted-foreground truncate">{m.name || "New material"}</label>
              <div>
                <Input id={`massume-${m.material_id}`} className={IN} value={m.assumption} placeholder="Half-brick wall, stretcher bond, 10mm joints"
                  onChange={e => setMat(i, { assumption: e.target.value })} data-testid={`input-material-assumption-${m.material_id}`} />
                <Err id={`material-assumption-${m.material_id}`} msg={errors[`materials.${i}.assumption`]} />
              </div>
            </div>
          ))}
        </div>
      )}

      {isFirstAdd && (
        <p className={`text-xs ${differs ? "text-destructive" : "text-muted-foreground"}`} data-testid={`text-first-add-hint-${taskId}`}>
          A first breakdown must add up to the current typical of {fmt(currentTypical)} exactly, so the price does not change. Use labour as the balancing figure.
        </p>
      )}
      {section && <p className="text-xs text-muted-foreground">Typical rate includes Labour, Materials and Plant. Changes in other sections are kept when you switch sections.</p>}
      {section && Object.keys(errors).some(key => key !== "_root") && <p className="text-sm text-destructive">Check the breakdown fields. Switch sections to correct any missing or invalid values before saving.</p>}
      {errors["_root"] && <p className="text-sm text-destructive" data-testid={`error-root-${taskId}`}>{errors["_root"]}</p>}
      {serverError && <p className="text-sm text-destructive" data-testid={`error-save-${taskId}`}>{serverError}</p>}
      {children}
    </div>
  );
}
